
import { defineComponent } from 'vue';

// API
import searchGIF, { API_KEY } from '@/api/search';
import { SearchResponseData } from '@/types/search.api.types';

// Components
import SearchBar from '@/components/SearchBar/SearchBar.vue';
import AbsoluteLoader from '@/components/Loader/AbsoluteLoader.vue';
import ButtonRouterLink from '@/components/Button/ButtonRouterLink.vue';

// Types
import { RecentSearch } from '@/types/search-results.types';

const ELEMENTS_PER_PAGE = 48;

type ResultsQuery = {
  q: string;
  page: string;
}

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $config: Record<string, unknown>;
  }
}

export default defineComponent({
  name: 'Results',
  components: {
    SearchBar,
    ButtonRouterLink,
    AbsoluteLoader
  },
  data() {
    return {
      loading: false,
      maxPages: 1,
      open: false,
      currentPage: 0,
      recentSearches: [] as RecentSearch[],
      gifData: {} as SearchResponseData,
      searchQuery: (this.$route.query as ResultsQuery).q
    };
  },
  methods: {
    onSearchChange(e: InputEvent) {
      this.searchQuery = (e.currentTarget as HTMLInputElement).value;
    },
    onSearchSubmit() {
      this.recentSearches.push({
        id: `${this.searchQuery.split(' ').join('_')}_${this.searchQuery.length}`,
        query: this.searchQuery,
        faved: false
      });

      localStorage.setItem('recentSearch', JSON.stringify(this.recentSearches));

      this.$router.push({
        path: '/search',
        query: {
          q: this.searchQuery
        }
      });
    },
    toggle() {
      this.open = !this.open;
    }
  },
  computed: {
    totalResults() {
      // More GIF items can be returned, but, as of Oct 17thm 2021, the max. value for the offset is 4999
      // https://developers.giphy.com/docs/api/endpoint/#search
      if (this.gifData.pagination && this.gifData.pagination.total_count > 4999) {
        return 4999;
      }
      return this.gifData.pagination ? this.gifData.pagination.total_count : 0;
    },
    singleResult() {
      return (this.gifData.pagination && this.gifData.pagination.total_count === 1) ? 'resultado' : 'resultados';
    },
    nextPage() {
      const searchObj = new URLSearchParams({
        q: (this.$route.query as ResultsQuery).q
      });

      if (this.$route.query.page) {
        const nextPage = parseInt(this.$route.query.page as string, 10) + 1;

        if (nextPage < this.maxPages) {
          searchObj.append('page', String(nextPage));
        }
      } else {
        searchObj.append('page', '1');
      }

      return `/search?${searchObj.toString()}`;
    },
    prevPage() {
      const searchObj = new URLSearchParams({
        q: (this.$route.query as ResultsQuery).q
      });

      if (this.$route.query.page) {
        const prevPage = parseInt(this.$route.query.page as string, 10) - 1;

        if (prevPage > 0) {
          searchObj.append('page', String(prevPage));
        }
      }

      return `/search?${searchObj.toString()}`;
    },
    shouldNextPageBeDisabled() {
      if (this.$route.query.page) {
        const nextPage = parseInt(this.$route.query.page as string, 10) + 1;

        if (nextPage > this.maxPages || this.gifData.data.length === 0 || Object.keys(this.gifData).length === 0) {
          return true;
        }
      }

      return false;
    },
    shouldPrevPageBeDisabled() {
      if (this.$route.query.page) {
        const prevPage = parseInt(this.$route.query.page as string, 10) - 1;

        if (prevPage >= 0) {
          return false;
        }
      }

      return true;
    }
  },
  created() {
    this.$watch(
      () => this.$route.query,
      (newQuery: ResultsQuery) => {
        this.loading = true;

        const res = searchGIF({
          api_key: API_KEY,
          q: `perritos ${decodeURIComponent(newQuery.q)}`,
          rating: 'pg',
          lang: 'es',
          limit: ELEMENTS_PER_PAGE,
          offset: newQuery.page ? parseInt(decodeURIComponent(newQuery.page), 10) * ELEMENTS_PER_PAGE : 0
        });

        res.then(response => {
          const totalResult = response.pagination.total_count > 4999 ? 4999 : response.pagination.total_count;

          this.gifData = { ...response };
          this.maxPages = totalResult > ELEMENTS_PER_PAGE ? Math.floor(totalResult / ELEMENTS_PER_PAGE) : 1;
        }).finally(() => {
          this.loading = false;
        });
      }
    );
  },
  mounted() {
    this.loading = true;

    const recentSearches = localStorage.getItem('recentSearch');

    if (recentSearches) {
      this.recentSearches = JSON.parse(recentSearches);
    }

    const res = searchGIF({
      api_key: API_KEY,
      q: `perritos ${decodeURIComponent((this.$route.query as ResultsQuery).q)}`,
      rating: 'pg',
      lang: 'es',
      limit: ELEMENTS_PER_PAGE,
      offset: (this.$route.query as ResultsQuery).page
        ? parseInt(decodeURIComponent((this.$route.query as ResultsQuery).page), 10) * ELEMENTS_PER_PAGE
        : 0
    });

    res.then(response => {
      const totalResult = response.pagination.total_count > 4999 ? 4999 : response.pagination.total_count;

      this.gifData = { ...response };
      this.maxPages = totalResult > ELEMENTS_PER_PAGE ? Math.floor(totalResult / ELEMENTS_PER_PAGE) : 1;
    }).finally(() => {
      this.loading = false;
    });
  }
});
